import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useToast } from '@/hooks/use-toast';
import { useAnalyticsContext } from '@/components/AnalyticsProvider';
import { EmailService, EmailData } from '@/services/emailService';
import { ReservationService } from '@/services/reservationService';
import { sanitizeFormData } from '@/utils/sanitize';
import { trackEnhancedReservation, trackEnhancedLead } from '@/components/GoogleAdsEnhanced';
import { gtag } from '@/utils/gtag';
import { triggerSuccessConfetti } from '@/utils/confetti';
import { logger } from '@/utils/logger';

const reservationSchema = z.object({
  name: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  phone: z.string().min(8, "Le numéro de téléphone doit contenir au moins 8 chiffres"),
  email: z.string().email("Adresse email invalide"),
  eventDate: z.string().min(1, "La date de l'événement est requise"),
  products: z.object({
    sumo: z.boolean(),
    chateau: z.boolean(),
  }),
  chateauType: z.string().optional(),
  message: z.string().optional(),
}).refine((data) => data.products.sumo || data.products.chateau, {
  message: "Vous devez sélectionner au moins un produit",
  path: ["products"],
});

export type ReservationFormData = z.infer<typeof reservationSchema>;

interface UseReservationFormProps {
  preSelectedChateau?: string;
  chateaux: any[];
  onSuccess?: () => void;
}

export const useReservationForm = ({ 
  preSelectedChateau, 
  chateaux,
  onSuccess 
}: UseReservationFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showChateauType, setShowChateauType] = useState(
    Boolean(preSelectedChateau && preSelectedChateau !== "sumos")
  );
  
  const { toast } = useToast();
  const { trackFormInteraction, trackFormSubmission } = useAnalyticsContext();

  const form = useForm<ReservationFormData>({
    resolver: zodResolver(reservationSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      eventDate: "",
      products: {
        sumo: preSelectedChateau === "sumos",
        chateau: Boolean(preSelectedChateau && preSelectedChateau !== "sumos"),
      },
      chateauType: (preSelectedChateau && preSelectedChateau !== "sumos") ? preSelectedChateau : "",
      message: "",
    },
  });

  const handleProductChange = (product: 'sumo' | 'chateau', checked: boolean) => {
    const currentProducts = form.getValues("products");
    form.setValue("products", { ...currentProducts, [product]: checked });
    
    if (product === 'chateau') {
      setShowChateauType(checked);
      if (!checked) {
        form.setValue("chateauType", "");
      }
    }
  };

  const trackConversions = (data: ReservationFormData) => {
    // Track enhanced conversion with client data
    gtag('event', 'conversion', {
      send_to: 'AW-1049756763/W1ONCJSag-kZEIOLqLMq',
      value: 60,
      currency: 'EUR',
      transaction_id: 'res_' + Date.now(),
      email: data.email,
      phone_number: data.phone,
      first_name: data.name?.split(' ')[0],
      last_name: data.name?.split(' ').slice(1).join(' '),
    });

    // Track dynamic conversion format
    gtag('event', 'conversion', {
      send_to: 'AW-1049756763/reservation_form_submit',
      value: 60,
      currency: 'EUR',
      event_category: 'reservation',
      event_label: 'website_form'
    });

    // Track lead generation
    gtag('event', 'generate_lead', {
      currency: 'EUR',
      value: 60,
      form_type: 'reservation',
      email: data.email,
      phone_number: data.phone
    });
  };

  const trackGoogleAdsConversions = (data: ReservationFormData) => {
    const estimatedPrice = ReservationService.getEstimatedPrice(data);
    const productType = ReservationService.getProductType(data, chateaux);
    
    trackEnhancedReservation({
      type: productType,
      price: estimatedPrice,
      email: data.email,
      phone: data.phone,
      name: data.name
    });
    
    trackEnhancedLead({
      form_type: 'reservation',
      email: data.email,
      phone: data.phone,
      name: data.name
    });
  };

  const resetForm = () => {
    setTimeout(() => {
      setShowSuccess(false);
      form.reset({
        name: "",
        phone: "",
        email: "",
        eventDate: "",
        products: {
          sumo: false,
          chateau: Boolean(preSelectedChateau),
        },
        chateauType: preSelectedChateau || "",
        message: "",
      });
      setShowChateauType(Boolean(preSelectedChateau));
    }, 3000);
  };

  const onSubmit = async (data: ReservationFormData) => {
    const formLogger = logger.createContext('ReservationForm');
    formLogger.info("Début soumission formulaire");
    formLogger.debug("Données reçues", data);

    try {
      setIsLoading(true);

      // Track form submission in analytics
      await trackFormSubmission('reservation', data);

      // Track conversions
      trackConversions(data);

      // Sanitize input data
      const sanitizedFormData = sanitizeFormData(data) as ReservationFormData;
      
      // Convert to EmailData format
      const emailData: EmailData = {
        name: sanitizedFormData.name || '',
        email: sanitizedFormData.email || '',
        phone: sanitizedFormData.phone || '',
        eventDate: sanitizedFormData.eventDate || '',
        products: {
          sumo: sanitizedFormData.products?.sumo || false,
          chateau: sanitizedFormData.products?.chateau || false
        },
        chateauType: sanitizedFormData.chateauType || '',
        message: sanitizedFormData.message || ''
      };

      // Send email
      const emailResult = await EmailService.sendReservationEmail(emailData, chateaux);

      // Always save to Google Sheets as backup
      await ReservationService.saveToGoogleSheets(emailData, chateaux);

      if (emailResult.success) {
        // Success - track Google Ads conversions
        trackGoogleAdsConversions(sanitizedFormData);

        // Show success feedback
        triggerSuccessConfetti();
        
        toast({
          title: "Demande envoyée !",
          description: "Votre demande de réservation a été envoyée avec succès. Nous vous recontacterons rapidement.",
        });

        setShowSuccess(true);
        resetForm();
        onSuccess?.();

      } else {
        // Email failed but data saved
        formLogger.error("Tous les systèmes d'email ont échoué");
        formLogger.info("Demande sauvegardée dans Google Sheets", emailData);
        
        // Still show success since data is saved
        triggerSuccessConfetti();
        
        toast({
          variant: "destructive", 
          title: "Problème d'envoi d'email",
          description: "Votre demande est sauvegardée dans nos systèmes. Nous vous contacterons par téléphone sous 24h.",
        });

        setShowSuccess(true);
        resetForm();
        onSuccess?.();
      }

    } catch (error: any) {
      formLogger.error("Erreur critique lors de la soumission", error);
      
      toast({
        title: "Erreur technique",
        description: "Veuillez nous contacter directement par téléphone. Vos données sont sauvegardées.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    isLoading,
    showSuccess,
    showChateauType,
    handleProductChange,
    onSubmit,
    chateauOptions: ReservationService.getChateauOptions(chateaux)
  };
};